/* eslint-disable */
import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import {
	initialChartTabs,
	initialTabs,
	initialDataFilterTabs,
} from './config';
import { fetchInitialData as fetchInitialDataApi } from './services';
import { createJSONStorage } from 'zustand/middleware';
import moment from 'moment';
import { ExternalLink, Info } from 'lucide-react';

const convertToLakhsCrores = (value) => {
	if (!value || value === '-') return value;

	// Extract number and unit
	const match = value.match(/₹([\d.]+)([KLMCr]*)/i);
	if (!match) return value;

	let num = parseFloat(match[1]);
	const unit = match[2];

	// Normalize to absolute number
	if (unit === 'K') num *= 1_000;
	else if (unit === 'L') num *= 1_00_000;
	else if (unit === 'Cr') num *= 1_00_00_000;

	// Convert
	if (num >= 1_00_00_000) {
		return `₹${(num / 1_00_00_000).toFixed(2)} Cr`;
	}
	if (num >= 1_00_000) {
		return `₹${(num / 1_00_000).toFixed(2)} L`;
	}

	return `₹${num.toFixed(0)}`;
};


export const useProductionAnalysisStore = create(
	subscribeWithSelector(
		persist(
			(set, get) => ({
				tabs: initialTabs,
				dataFilterTabs: initialDataFilterTabs,
				selectedTab: initialTabs?.[0]?.value,

				perUnitToggle: false,
				dateRangeObj: {
					from: new Date(moment().startOf('month')),
					to: new Date(),
				},

				chartTabs: initialChartTabs,
				selectedChartTab: initialChartTabs?.[0]?.value,
				dashboardMode: 'batchPerformance',
				loading: false,
				dateFilter: {
					year: null,
					month: null,
					day: null,
					hour: null,
					week: null,
					currentSelection: null,
				},
				grouping: {
					active: true,
					type: null,
					currentSelection: '',
					selection: {},
				},
				filters: {},
				cardsData: [],
				chartData: {},
				isCache: false,
				cacheTime: null,

				setCacheTime: (cacheTime) =>
					set({ cacheTime }),
				setIsCache: (isCache) =>
					set({ isCache }),
				setSelectedTab: (tabValue) =>
					set({ selectedTab: tabValue }),

				setSelectedTimeFilter: (timeFilterValue) =>
					set({ selectedTimeFilter: timeFilterValue }),

				setSelectedChartTab: (chartTabValue) =>
					set({ selectedChartTab: chartTabValue }),

				setDashboardMode: (dashboardMode) =>
					set({ dashboardMode }),

				setPerUnitToggle: (perUnitToggle) =>
					set({ perUnitToggle }),

				setDateRangeObj: (dateRangeObj) =>
					set({ dateRangeObj }),
				setDateFilter: (dateFilter) =>
					set({ dateFilter: { ...dateFilter } }),
				setGrouping: (grouping) =>
					set({ grouping: { ...grouping } }),
				setFilters: (filters) =>
					set({ filters: { ...filters } }),
				fetchInitialData: async (refresh = false) => {
					const state = get();
					set({ loading: true });

					try {
						// Pass refresh flag if supported by API service, or modify state temporary if required
						// Assuming fetchInitialDataApi accepts the state object which we can augment with NO_CACHE
						const requestState = refresh ? { ...state, NO_CACHE: true } : state;

						const response = await fetchInitialDataApi(requestState);
						let statsArray = Object.values(response?.statsData ?? {});
						if (state.selectedChartTab === 'TotalCost') {
							statsArray = statsArray.map(item => {
								if (item.label === 'Per Unit Production Cost') return item;
								return {
									...item,
									value: item.value,
								}
							});
						}
						statsArray = statsArray.map(item => {
							const labelLower = item.label.toLowerCase();
							const isRedirection = labelLower.includes('ega');
							const isMeasuredProduction = labelLower.includes('measured production');
							return {
								...item,
								...(isRedirection ? {
									redirect: true,
									icon: ExternalLink,
									tooltipLabel: 'Go to Quality Analysis',
									link: `/console2/analytics/quality/new/analysis?selections=${encodeURIComponent(JSON.stringify({ tab: 'production' }))}`
								} : {}),
								...(isMeasuredProduction ? {
									hasModalClick: true,
									icon: Info,
									tooltipLabel: 'View Details'
								} : {}),
								value: item.value,
							}
						});
						set({
							cardsData: statsArray,
							chartData: response?.chartData ?? {},
							loading: false,
							isCache: false,
							cacheTime: response?.cacheTime,
						});
					} catch (error) {
						console.error('Error fetching initial data:', error);
						set({
							cardsData: [],
							chartData: {},
							loading: false,
							isCache: false,
						});
					}
				},
				setupSubscriptions: () => {
					const store = useProductionAnalysisStore;

					// avoid setting up more than once
					if (store.__hasSubscriptions) return;
					store.__hasSubscriptions = true;

					let timeoutId = null;
					const triggerFetch = () => {
						if (timeoutId) clearTimeout(timeoutId);
						timeoutId = setTimeout(() => {
							store.getState().fetchInitialData();
						}, 0);
					};

					const keys = [
						'selectedTab',
						'selectedChartTab',
						'dateRangeObj',
						'perUnitToggle',
						'dateFilter',
						'grouping',
						'filters',
						'dashboardMode',
					];

					keys.forEach((key) => {
						store.subscribe(
							(s) => s[key],
							triggerFetch
						);
					});
				},
			}),
			{
				name: 'productionAnalysisStore',
				storage: createJSONStorage(() => sessionStorage),
				deserialize: (str) => {
					const parsed = JSON.parse(str);
					if (!parsed?.state) return parsed;
					const state = parsed.state;
					return {
						...parsed,
						state: {
							...state,
							dateRangeObj: state.dateRangeObj
								? {
									from: new Date(state.dateRangeObj.from),
									to: new Date(state.dateRangeObj.to),
								}
								: null,
							dateFilter: state.dateFilter ? { ...state.dateFilter } : null,
							grouping: state.grouping ? { ...state.grouping } : null,
							filters: state.filters ? { ...state.filters } : null,
						},
					};
				},
				partialize: (state) => ({
					selectedTab: state.selectedTab,
					selectedChartTab: state.selectedChartTab,
					perUnitToggle: state.perUnitToggle,
					dateRangeObj: state.dateRangeObj,
					dateFilter: state.dateFilter,
					grouping: state.grouping,
					filters: state.filters,
					dashboardMode: state.dashboardMode,
				}),
			}
		)
	)
);